import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Clapperboard, Play, Search, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listVideos, deleteVideo, bulkDeleteVideos, rateVideo, listImports, importYouTube, clearImports } from "../lib/api";
import { formatDate, PLATFORM_LABELS } from "../lib/youtube";
import { StarRating } from "../components/StarRating";

const rowVariants = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

export default function History() {
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [topRated, setTopRated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    listVideos()
      .then(setVideos)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    await deleteVideo(id).catch(() => {});
    setVideos((vs) => vs.filter((v) => v.video_id !== id));
  };

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkRemove = async () => {
    setDeleting(true);
    await bulkDeleteVideos([...selected]).catch(() => {});
    setVideos((vs) => vs.filter((v) => !selected.has(v.video_id)));
    setSelected(new Set());
    setDeleting(false);
    setConfirming(false);
  };

  const rate = async (id, rating) => {
    setVideos((vs) => vs.map((v) => (v.video_id === id ? { ...v, rating } : v)));
    await rateVideo(id, rating).catch(() => {});
  };

  const [imports, setImports] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    listImports().then(setImports).catch(() => {});
  }, []);

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const res = await importYouTube(file);
      toast.success(`Imported ${res.imported} new watches`);
      setImports(await listImports().catch(() => []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const clearAllImports = async () => {
    await clearImports().catch(() => {});
    setImports([]);
  };

  const filtered = videos.filter(
    (v) =>
      v.title.toLowerCase().includes(query.trim().toLowerCase()) &&
      (!topRated || (v.rating || 0) >= 4)
  );

  return (
    <div data-testid="history-page" className="max-w-5xl mx-auto py-16 px-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <p className="font-meta text-[10px] uppercase tracking-[0.35em] text-zinc-600 mb-3">The Archive</p>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-white mb-4">Screening History</h1>
        <p className="font-meta text-xs text-zinc-500 mb-8" data-testid="history-count">
          {query ? `${filtered.length} OF ${videos.length}` : videos.length} {videos.length === 1 ? "FILM" : "FILMS"} LOGGED
        </p>
        <div className="mb-8 flex items-center gap-4 flex-wrap">
          <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-full px-5 py-2.5 flex items-center gap-3 focus-within:border-white/30 focus-within:bg-white/10 transition-colors duration-300">
            <Search className="w-4 h-4 text-zinc-500 shrink-0" />
            <input
              data-testid="history-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your archive by title…"
              className="bg-transparent border-none text-white placeholder-zinc-500 focus:outline-none focus:ring-0 w-full font-ui text-sm"
              spellCheck={false}
            />
          </div>
          <button
            data-testid="top-rated-filter"
            onClick={() => setTopRated(!topRated)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full border font-meta text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
              topRated
                ? "border-amber-400/50 bg-amber-400/10 text-amber-300"
                : "border-white/10 text-zinc-500 hover:text-white hover:border-white/30"
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${topRated ? "fill-amber-400 text-amber-400" : ""}`} />
            4★ &amp; up
          </button>
        </div>
        {selected.size > 0 && (
          <div data-testid="bulk-delete-bar" className="mb-8 flex items-center gap-5">
            <span className="font-meta text-xs tracking-[0.2em] text-zinc-400 uppercase" data-testid="selected-count">
              {selected.size} selected
            </span>
            <button
              data-testid="bulk-delete-button"
              onClick={() => setConfirming(true)}
              className="px-5 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 font-meta text-xs uppercase tracking-widest hover:bg-red-500/20 transition-colors duration-200"
            >
              Delete selected
            </button>
            <button
              data-testid="clear-selection-button"
              onClick={() => setSelected(new Set())}
              className="font-meta text-xs uppercase tracking-widest text-zinc-500 hover:text-white transition-colors duration-200"
            >
              Clear
            </button>
          </div>
        )}
      </motion.div>

      {loaded && videos.length === 0 && (
        <div data-testid="history-empty" className="py-24 text-center">
          <p className="font-heading text-2xl text-zinc-600">The archive is empty.</p>
          <p className="mt-3 font-meta text-xs uppercase tracking-[0.25em] text-zinc-700">Play something and it will appear here</p>
        </div>
      )}

      {loaded && videos.length > 0 && filtered.length === 0 && (
        <div data-testid="history-no-results" className="py-24 text-center">
          <p className="font-heading text-2xl text-zinc-600">
            {query ? `No titles match "${query}".` : "No top-rated films yet."}
          </p>
          <p className="mt-3 font-meta text-xs uppercase tracking-[0.25em] text-zinc-700">
            {query ? "Try a different search" : "Rate a film 4 stars or more to see it here"}
          </p>
        </div>
      )}

      <div className="flex flex-col">
        {filtered.map((video, i) => (
          <motion.div
            key={video.video_id}
            custom={i}
            variants={rowVariants}
            initial="hidden"
            animate="show"
            data-testid={`history-item-${video.video_id}`}
            onClick={() => navigate(`/?v=${video.video_id}`)}
            className="group grid grid-cols-1 md:grid-cols-12 gap-6 p-4 -mx-4 rounded-2xl hover:bg-white/[0.02] transition-colors duration-300 border-b border-white/5 last:border-none items-center cursor-pointer"
          >
            <div className="col-span-1 flex md:justify-center items-center">
              <button
                data-testid={`select-checkbox-${video.video_id}`}
                onClick={(e) => toggleSelect(e, video.video_id)}
                aria-label="Select for deletion"
                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors duration-200 ${
                  selected.has(video.video_id)
                    ? "bg-white border-white text-black"
                    : "border-white/20 text-transparent hover:border-white/50"
                }`}
              >
                <Check className="w-3 h-3" strokeWidth={3.5} />
              </button>
            </div>
            <div className="col-span-1 md:col-span-3 aspect-video rounded-lg overflow-hidden bg-zinc-900 relative">
              {video.thumbnail ? (
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                  <Clapperboard className="w-8 h-8" strokeWidth={1} />
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                <Play className="w-8 h-8 text-white fill-white" />
              </div>
            </div>
            <div className="col-span-1 md:col-span-7 flex flex-col gap-2">
              <h2 className="text-xl md:text-2xl font-heading text-white line-clamp-2">{video.title}</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-meta text-xs text-zinc-500 tracking-widest">{formatDate(video.added_at)}</span>
                <span
                  data-testid={`platform-badge-${video.video_id}`}
                  className="font-meta text-[10px] tracking-[0.2em] text-zinc-600 border border-white/10 rounded-full px-2.5 py-0.5"
                >
                  {PLATFORM_LABELS[video.platform] || "VIDEO"}
                </span>
              </div>
              <StarRating
                testid={`rating-${video.video_id}`}
                value={video.rating || 0}
                onRate={(n) => rate(video.video_id, n)}
              />
            </div>
            <div className="col-span-1 md:col-span-1 flex justify-end md:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <button
                data-testid={`delete-video-${video.video_id}`}
                onClick={(e) => remove(e, video.video_id)}
                aria-label="Remove from history"
                className="p-3 rounded-full text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-20" data-testid="youtube-import-section">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <p className="font-meta text-[10px] uppercase tracking-[0.35em] text-zinc-600 mb-2">Imported</p>
            <h2 className="text-2xl md:text-3xl font-heading text-zinc-300 tracking-tight">YouTube Watch History</h2>
            <p className="font-meta text-[10px] text-zinc-600 mt-2 tracking-[0.15em] uppercase">
              From Google Takeout — separate from your PurePlay screenings
            </p>
          </div>
          <div className="flex items-center gap-3">
            {imports.length > 0 && (
              <button
                data-testid="clear-imports-button"
                onClick={clearAllImports}
                className="px-4 py-2 rounded-full border border-white/10 font-meta text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 hover:border-red-500/30 transition-colors duration-200"
              >
                Clear
              </button>
            )}
            <label
              data-testid="import-takeout-button"
              className="px-4 py-2 rounded-full border border-white/10 font-meta text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:border-white/30 transition-colors duration-200 cursor-pointer"
            >
              {importing ? "Importing…" : "Import Takeout"}
              <input type="file" accept=".html,text/html" className="hidden" onChange={onImportFile} disabled={importing} />
            </label>
          </div>
        </div>
        {imports.length === 0 ? (
          <p className="text-sm text-zinc-600 border border-dashed border-white/10 rounded-2xl p-8 text-center" data-testid="import-empty">
            Nothing imported yet. In Google Takeout, export YouTube → history → watch-history.html and upload it here.
          </p>
        ) : (
          <div className="flex flex-col border border-dashed border-white/10 rounded-2xl overflow-hidden">
            {imports.map((w) => (
              <button
                key={`${w.video_id}-${w.watched_at}`}
                data-testid={`import-item-${w.video_id}`}
                onClick={() => navigate(`/?v=${w.video_id}`)}
                className="flex items-center justify-between gap-4 px-5 py-3.5 text-left border-b border-white/5 last:border-none hover:bg-white/[0.02] transition-colors duration-200"
              >
                <span className="text-sm text-zinc-400 truncate">{w.title}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="font-meta text-[10px] tracking-[0.15em] text-zinc-600 uppercase border border-white/10 rounded-full px-2 py-0.5">
                    YT Import
                  </span>
                  <span className="font-meta text-[10px] text-zinc-600">{w.watched_at}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {confirming && (
          <motion.div
            data-testid="delete-confirm-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirming(false)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 max-w-sm w-full"
            >
              <h3 className="font-heading text-2xl text-white tracking-tight">
                Delete {selected.size} {selected.size === 1 ? "film" : "films"}?
              </h3>
              <p className="mt-3 text-sm text-zinc-500">
                This permanently removes the selected screenings from your archive.
              </p>
              <div className="mt-8 flex justify-end gap-3">
                <button
                  data-testid="cancel-delete-button"
                  onClick={() => setConfirming(false)}
                  className="px-5 py-2.5 rounded-full border border-white/10 font-meta text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:border-white/30 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  data-testid="confirm-delete-button"
                  onClick={bulkRemove}
                  disabled={deleting}
                  className="px-5 py-2.5 rounded-full bg-red-500 font-meta text-[11px] uppercase tracking-[0.2em] text-white hover:bg-red-600 transition-colors duration-200 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
